/**
 * Retry logic for New Relic error reporting
 * @param {Function} reportFn - Function that reports to New Relic
 * @param {number} retryCount - Current retry attempt
 */
const reportToNewRelicWithRetry = (reportFn, retryCount = 0) => {
  if (typeof window !== 'undefined' && window.newrelic) {
    reportFn();
  } else if (retryCount < 3) {
    setTimeout(() => reportToNewRelicWithRetry(reportFn, retryCount + 1), 1000);
  }
};

/**
 * Initialize global error handlers for New Relic
 * Call this once when the app starts (client-side only)
 * Sets up listeners immediately, retries if New Relic not available
 */
export const setupErrorTracking = () => {
  // Skip if running on server-side
  if (typeof window === 'undefined') {
    return;
  }

  // Global JavaScript error handler
  window.addEventListener('error', (event) => {
    const reportError = () => {
      const error = event.error || new Error(event.message || 'Unknown error');
      window.newrelic.noticeError(error, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        source: 'global_error_handler',
        message: event.message,
      });
    };
    reportToNewRelicWithRetry(reportError);
  });

  // Unhandled promise rejection handler
  window.addEventListener('unhandledrejection', (event) => {
    const reportError = () => {
      const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
      window.newrelic.noticeError(error, {
        source: 'unhandled_promise_rejection',
      });
    };
    reportToNewRelicWithRetry(reportError);
  });
};

/**
 * Manually track an error to New Relic
 * @param {Error} error - The error to track
 * @param {Object} context - Additional context/attributes
 */
export const trackError = (error, context = {}) => {
  const reportError = () => {
    window.newrelic.noticeError(error, {
      ...context,
      source: 'manual_tracking',
    });
  };
  reportToNewRelicWithRetry(reportError);
};

/**
 * Report ErrorBoundary errors to New Relic
 * @param {Error} error - The error from componentDidCatch
 * @param {Object} info - The error info from componentDidCatch
 */
export const reportErrorBoundaryError = (error, info) => {
  const reportError = () => {
    window.newrelic.noticeError(error, {
      componentStack: info.componentStack,
      source: 'react_error_boundary',
    });
  };
  reportToNewRelicWithRetry(reportError);
};
